import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import {
  GetProductsByIdBatchedQuery,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { IProduct } from '@containers/ProductListing/model';
import { RecommendationPlacement } from '@utils/tracking/recommendationPlacement';
import { RecommendationAttributesInput } from '@utils/tracking/recommendationTracking';

import { dispatchProductsShownEvent } from '../hooks/useDispatchProductsShowEvent';
import { ISectionContainerProps, SectionContainer } from '../SectionContainer';

import { ProductListContent } from './ProductItemsContent';
import { useProductsByIdsBatched } from './useProductsByIdsBatched';

export type VpProductItem = NonNullable<
  GetProductsByIdBatchedQuery['vpProductByIds'][number]
>;

export interface IProductsSectionProps
  extends Omit<ISectionContainerProps, 'children'> {
  productIds: Array<IProduct['id']>;
  /**
   * Pre-resolved products to render as-is. When provided, ProductsSection skips its
   * own `vpProductByIds` request and renders these directly (e.g. products already
   * returned by the sponsored-ads query). `productIds` is then only used for gating.
   */
  products?: VpProductItem[];
  idType?: ProductIdType;
  filterProducts?: (product: VpProductItem) => boolean;
  productIdsLoading?: boolean;
  trackProductClick?: boolean;
  trackProductImpression?: boolean;
  attributionToken?: string;
  itemsPerSlide?: number;
  titleClassName?: string;
  listContentClassName?: string;
  isSponsored?: boolean;
  onProductClick?: (productId: string) => void;
  trackProductClickOnContextMenu?: boolean;
  onProductImpression?: (productId: string) => void;
  onProductBasketChange?: (productId: string, quantity: number) => void;
  onProductWishlist?: (productId: string, wishlisted: boolean) => void;
  recommendationPlacement?: RecommendationPlacement;
  recommendationAttributes?: RecommendationAttributesInput;
}

export const ProductsSection: React.FC<IProductsSectionProps> = React.memo(
  ({
    productIds,
    products,
    idType,
    filterProducts,
    listLabel = '',
    containerId = '',
    titleId = '',
    productIdsLoading = false,
    titleMessage = null,
    withDivider = false,
    trackProductClick = false,
    trackProductImpression = false,
    attributionToken,
    itemsPerSlide,
    titleClassName,
    listContentClassName,
    isSponsored = false,
    onProductClick,
    trackProductClickOnContextMenu = false,
    onProductImpression,
    onProductBasketChange,
    onProductWishlist,
    recommendationPlacement,
    recommendationAttributes,
  }) => {
    const [ref, inView] = useInView({ triggerOnce: true });

    // Pre-resolved products render as-is; otherwise resolve the ids ourselves.
    const hasResolvedProducts = products !== undefined;

    const { loading: queryLoading, data: rawData } = useProductsByIdsBatched(
      productIds,
      {
        idType,
        skip: hasResolvedProducts || !inView || productIdsLoading,
        onCompleted: (response) => {
          dispatchProductsShownEvent(
            response?.vpProductByIds
              .filter((p): p is NonNullable<typeof p> => p !== null)
              .filter((p) => !filterProducts || filterProducts(p))
              .map((p) => ({ ...p, webId: p.id! })),
            {
              section: listLabel,
            }
          );
        },
      }
    );

    const loading = hasResolvedProducts ? false : queryLoading;

    const data = React.useMemo(() => {
      const base = hasResolvedProducts ? { vpProductByIds: products } : rawData;
      if (!base || !filterProducts) {
        return base;
      }
      return {
        ...base,
        vpProductByIds: base.vpProductByIds?.filter(
          (p) => !!p && filterProducts(p)
        ),
      };
    }, [hasResolvedProducts, products, rawData, filterProducts]);

    // The query path reports "products shown" from its onCompleted; the pre-resolved
    // path has no query, so fire the same event once the section is in view.
    const shownReportedRef = React.useRef(false);
    React.useEffect(() => {
      if (
        !hasResolvedProducts ||
        !inView ||
        shownReportedRef.current ||
        !products?.length
      ) {
        return;
      }
      shownReportedRef.current = true;
      dispatchProductsShownEvent(
        products
          .filter((p): p is NonNullable<typeof p> => p !== null)
          .filter((p) => !filterProducts || filterProducts(p))
          .map((p) => ({ ...p, webId: p.id! })),
        { section: listLabel }
      );
    }, [hasResolvedProducts, inView, products, filterProducts, listLabel]);

    const noProducts = !loading && !data?.vpProductByIds?.length;
    if (noProducts && inView && !productIdsLoading) {
      return null;
    }

    return (
      <SectionContainer
        containerId={containerId}
        key={containerId}
        withDivider={withDivider}
        titleMessage={titleMessage ?? undefined}
        titleId={titleId}
        ref={ref}
        titleClassName={titleClassName}
      >
        <ProductListContent
          className={listContentClassName}
          itemsPerSlide={itemsPerSlide}
          loading={loading || productIdsLoading}
          inView={inView}
          data={data}
          trackProductClick={trackProductClick}
          trackProductImpression={trackProductImpression}
          listLabel={listLabel}
          attributionToken={attributionToken}
          isSponsored={isSponsored}
          onProductClick={onProductClick}
          trackProductClickOnContextMenu={trackProductClickOnContextMenu}
          onProductImpression={onProductImpression}
          onProductBasketChange={onProductBasketChange}
          onProductWishlist={onProductWishlist}
          recommendationPlacement={recommendationPlacement}
          recommendationAttributes={recommendationAttributes}
        />
      </SectionContainer>
    );
  }
);
